<template>
	<define-template>
		<!-- Single image -->
		<div
			ref="thumbnailContainer"
			class="relative overflow-hidden rounded"
			:class="{
				'group-hover:opacity-90': videoUrl,
			}"
		>
			<!-- eslint-disable tailwindcss/no-custom-classname -->
			<nuxt-picture
				v-if="!ad.use_as_slideshow && ad.background_image?.filename !== null"
				:src="ad.background_image?.filename"
				:alt="ad.background_image.alt ?? ad.title"
				loading="lazy"
				provider="storyblok"
				sizes="xs:50vw sm:316px md:245px lg:244px xl:308px 2xl:372px"
				class="thumbnail block size-full"
				:img-attrs="{
					class: 'size-full object-cover aspect-square',
				}"
			/>

			<span
				v-if="videoUrl"
				class="pointer-events-none absolute top-1/2 left-1/2 -translate-1/2 rounded-full bg-white text-[60px] leading-none"
			>
				<v-icon icon="play" class="inline" />
			</span>
		</div>

		<!-- Carousel -->
		<template v-if="ad.images && ad.images.length > 0 && ad.use_as_slideshow">
			<v-auto-scroller :items="ad.images">
				<template #item="{filename, alt, title}">
					<nuxt-picture
						:src="filename"
						:alt="alt ?? title"
						provider="storyblok"
						loading="lazy"
						sizes="xs:50vw sm:316px md:245px lg:244px xl:308px 2xl:372px"
						class="block size-full"
						:img-attrs="{
							class: 'size-full rounded object-cover aspect-square',
						}"
					/>
				</template>
			</v-auto-scroller>
		</template>

		<div class="p-2">
			<v-subtitle
				class="truncate leading-4 font-bold tracking-xl md:text-base md:leading-5"
				:class="{
					'group-hover:underline': url || videoUrl,
				}"
				size="sm"
			>
				{{ ad.title }}
			</v-subtitle>

			<p class="font-sans text-sm text-gray-500 md:text-base">
				{{ ad.content }}
			</p>
		</div>
	</define-template>

	<div v-if="videoUrl" class="flex-y group relative">
		<reuse-template />

		<button
			type="button"
			:aria-label="ad.title"
			class="absolute inset-0 z-10 cursor-pointer"
			@click="openLightbox($event)"
		></button>
	</div>

	<div v-else-if="url" class="group relative">
		<reuse-template />

		<nuxt-link
			:to="url"
			:aria-label="ad.title"
			class="absolute inset-0 z-10"
		></nuxt-link>
	</div>

	<div v-else class="relative">
		<reuse-template />
	</div>
</template>

<script lang="ts" setup>
import type {ListingAdComponent} from "@pkg/shared/types";
import PhotoSwipe from "photoswipe";
import PhotoSwipeLightbox from "photoswipe/lightbox";
import {createReusableTemplate} from "@vueuse/core";
import {getVideoPlayerUrl} from "@pkg/shared/composables/use-product-gallery";

import {useGallery} from "@pkg/shared/composables/use-gallery";
import formatSbUrl from "@pkg/shared/utils/format-sb-url";

const props = defineProps<{
	ad: ListingAdComponent;
}>();

const [defineTemplate, reuseTemplate] = createReusableTemplate();

const thumbnailContainer = ref<HTMLDivElement>();

const url = computed(() =>
	props.ad.link?.cached_url ? formatSbUrl(props.ad.link) : undefined,
);

// Note that this won't need to be reactive, no need to introduce video on-the-fly to the ad
const videoUrl = props.ad.video_link?.vimeo_raw;

const openLightbox = ref<(event: Event) => void>(() => {});

if (videoUrl) {
	const lightBoxParameters = computed(() => ({
		gallery: thumbnailContainer.value,
		// Lightbox turns elements that match this query selector into gallery elements, makes them clickable and fetches their display info from their data attributes.
		children: ".thumbnail",
		pswpModule: PhotoSwipe,

		showHideAnimationType: "fade" as const,
		bgOpacity: 1,
		padding: {
			top: 80,
			right: 80,
			bottom: 80,
			left: 80,
		},
	}));

	// Super lazy and dirty. Since we have only the one video which we render a lightbox for, we override photoswipe's rendering logic here
	const lightBoxOnInit = (lightBox: PhotoSwipeLightbox) => {
		lightBox.on("contentLoad", (event) => {
			const {content} = event;

			// prevent the deafult behavior
			event.preventDefault();

			// Create a container for iframe
			// and assign it to the `content.element` property
			content.element = document.createElement("div");
			content.element.className = "pswp__iframe-container";

			const iframe = document.createElement("iframe");
			iframe.setAttribute("allowfullscreen", "");

			iframe.setAttribute(
				"allow",
				"autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share",
			);
			iframe.src = `${getVideoPlayerUrl(videoUrl)}?autoplay=1`;
			content.element.append(iframe);
		});
	};

	const {lightBox} = useGallery({lightBoxParameters, onInit: lightBoxOnInit});

	openLightbox.value = (event: Event) => {
		event.stopPropagation();
		if (lightBox.value) {
			lightBox.value.loadAndOpen(0);
		}
	};
}
</script>

<style lang="scss">
.pswp {
	--pswp-bg: #fff;
	--pswp-icon-color: #ef1c35;
	--pswp-icon-color-secondary: #fff;
	--pswp-icon-stroke-width: 2px;
	--pswp-icon-stroke-color: #ef1c35;

	&__iframe-container {
		position: relative;
		width: 100%;
		height: 100%;
		pointer-events: none;
	}

	&__iframe-container iframe {
		width: 100%;
		height: 100%;
		max-width: 1200px;
		max-height: 1900px;
		pointer-events: auto;
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
	}
}
</style>
